// =============================================================================
// Colors Page — DB-backed color library (palette = brand color chart)
// =============================================================================
// Manages ColorPalette / ColorPaletteEntry. Palettes group brand charts
// (Gildan, Comfort Colors, Bella+Canvas…) so the same color name can map to
// different hexes per brand. Entries feed the server-side hex resolver used
// by apparel auto-detect, OptionValue hex auto-fill and designer swatches.
// =============================================================================

import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Spinner,
  Modal,
  ModalFooter,
  useConfirm,
} from '@tkpod/ui';
import { resolveColorNameToHex } from '@tkpod/shared';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { AlertBanner } from '@/components/AlertBanner';

// ---------------------------------------------------------------------------
// Types (API response shapes from ColorLibraryService)
// ---------------------------------------------------------------------------

interface PaletteSummary {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  colorCount: number;
  templateCount: number;
}

interface ColorEntry {
  id: string;
  paletteId: string;
  name: string;
  hexCode: string;
  hexCode2: string | null;
  swatchUrl: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface PaletteDetail extends Omit<PaletteSummary, 'colorCount'> {
  colors: ColorEntry[];
}

interface BulkImportLine {
  line: number;
  raw: string;
  name: string | null;
  hexCode: string | null;
  status: 'valid' | 'invalid' | 'duplicate-in-input' | 'duplicate-in-palette';
  existingHexCode?: string;
  error?: string;
}

interface BulkImportReport {
  dryRun: boolean;
  totalLines: number;
  validCount: number;
  invalidCount: number;
  duplicateCount: number;
  lines: BulkImportLine[];
  created?: number;
  updated?: number;
  skipped?: number;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function Swatch({ hex, hex2, size = 'md' }: { hex: string; hex2?: string | null; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-5 w-5' : 'h-7 w-7';
  const style = hex2
    ? { background: `linear-gradient(135deg, ${hex} 50%, ${hex2} 50%)` }
    : { background: hex };
  return (
    <span
      className={`inline-block ${dim} shrink-0 rounded border border-border shadow-sm`}
      style={style}
      title={hex2 ? `${hex} / ${hex2}` : hex}
    />
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ColorsPage() {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreatePalette, setShowCreatePalette] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [editEntry, setEditEntry] = useState<ColorEntry | null>(null);

  const { data: palettes = [], isLoading } = useQuery<PaletteSummary[]>({
    queryKey: ['color-palettes'],
    queryFn: () => api.get<PaletteSummary[]>('/api/v1/admin/colors/palettes'),
  });

  const activePaletteId = selectedId ?? palettes[0]?.id ?? null;

  const { data: palette, isLoading: isDetailLoading } = useQuery<PaletteDetail>({
    queryKey: ['color-palette', activePaletteId],
    queryFn: () => api.get<PaletteDetail>(`/api/v1/admin/colors/palettes/${activePaletteId}`),
    enabled: !!activePaletteId,
  });

  const invalidatePalettes = () => {
    queryClient.invalidateQueries({ queryKey: ['color-palettes'] });
    if (activePaletteId) {
      queryClient.invalidateQueries({ queryKey: ['color-palette', activePaletteId] });
    }
  };

  const deletePaletteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/colors/palettes/${id}`),
    onSuccess: () => {
      toast('Palette deleted.');
      setSelectedId(null);
      invalidatePalettes();
    },
    onError: (err: Error) => toast(err.message || 'Failed to delete palette.', 'error'),
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/colors/entries/${id}`),
    onSuccess: () => {
      toast('Color removed.');
      invalidatePalettes();
    },
    onError: (err: Error) => toast(err.message || 'Failed to delete color.', 'error'),
  });

  const handleDeletePalette = async (p: PaletteSummary) => {
    const ok = await confirm({
      title: 'Delete palette',
      description: `"${p.name}" and its ${p.colorCount} color(s) will be permanently removed.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) deletePaletteMutation.mutate(p.id);
  };

  const handleDeleteEntry = async (entry: ColorEntry) => {
    const ok = await confirm({
      title: 'Delete color',
      description: `"${entry.name}" (${entry.hexCode}) will be removed from this palette.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) deleteEntryMutation.mutate(entry.id);
  };

  return (
    <div className="space-y-6">
      {dialog}
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Link to="/variant-options" className="hover:text-brand-600">Variant Options</Link>
        <span>/</span>
        <span className="text-text-primary">Color Library</span>
      </div>
      <Card>
        <CardHeader
          title="Color Library"
          description="Brand color charts (palette = brand). Entries power hex auto-detect, designer swatches and mask backgrounds."
          action={
            <Button size="sm" onClick={() => setShowCreatePalette(true)}>
              New Palette
            </Button>
          }
        />

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : palettes.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-sm text-text-tertiary">
            <p>No color palettes yet. Create one per brand chart (e.g. “Gildan”, “Comfort Colors”).</p>
            <Button size="sm" onClick={() => setShowCreatePalette(true)}>
              Create your first palette
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 px-6 pb-6 lg:grid-cols-[260px_1fr]">
            {/* Palette list */}
            <div className="space-y-1.5">
              {palettes.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full rounded-md border px-3 py-2.5 text-left transition-colors ${
                    p.id === activePaletteId
                      ? 'border-brand-500 bg-brand-50/50'
                      : 'border-border hover:bg-surface-secondary/60'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-text-primary">{p.name}</span>
                    {!p.isActive && <Badge variant="default" size="sm">Inactive</Badge>}
                  </div>
                  <div className="mt-0.5 text-xs text-text-tertiary">
                    {p.colorCount} color{p.colorCount === 1 ? '' : 's'}
                    {p.templateCount > 0 && ` · ${p.templateCount} template(s)`}
                  </div>
                </button>
              ))}
            </div>

            {/* Palette detail */}
            <div>
              {isDetailLoading || !palette ? (
                <div className="flex justify-center py-16">
                  <Spinner size="lg" />
                </div>
              ) : (
                <PaletteDetailPanel
                  palette={palette}
                  onBulkImport={() => setShowBulkImport(true)}
                  onEditEntry={setEditEntry}
                  onDeleteEntry={handleDeleteEntry}
                  onDeletePalette={() =>
                    handleDeletePalette({
                      ...palette,
                      colorCount: palette.colors.length,
                    })
                  }
                  onChanged={invalidatePalettes}
                />
              )}
            </div>
          </div>
        )}
      </Card>

      {showCreatePalette && (
        <CreatePaletteModal
          onClose={() => setShowCreatePalette(false)}
          onCreated={(id) => {
            setShowCreatePalette(false);
            setSelectedId(id);
            invalidatePalettes();
          }}
        />
      )}

      {showBulkImport && activePaletteId && (
        <BulkImportModal
          paletteId={activePaletteId}
          onClose={() => setShowBulkImport(false)}
          onImported={() => {
            setShowBulkImport(false);
            invalidatePalettes();
          }}
        />
      )}

      {editEntry && (
        <EditEntryModal
          entry={editEntry}
          onClose={() => setEditEntry(null)}
          onSaved={() => {
            setEditEntry(null);
            invalidatePalettes();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Palette detail panel — entries table + inline single add
// ---------------------------------------------------------------------------

function PaletteDetailPanel({
  palette,
  onBulkImport,
  onEditEntry,
  onDeleteEntry,
  onDeletePalette,
  onChanged,
}: {
  palette: PaletteDetail;
  onBulkImport: () => void;
  onEditEntry: (entry: ColorEntry) => void;
  onDeleteEntry: (entry: ColorEntry) => void;
  onDeletePalette: () => void;
  onChanged: () => void;
}) {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return palette.colors;
    return palette.colors.filter(
      (c) => c.name.toLowerCase().includes(q) || c.hexCode.toLowerCase().includes(q),
    );
  }, [palette.colors, filter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-text-primary">{palette.name}</h3>
          {palette.description && (
            <p className="text-xs text-text-tertiary">{palette.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onBulkImport}>
            Bulk Import
          </Button>
          <button
            type="button"
            onClick={onDeletePalette}
            className="inline-flex items-center rounded-md border border-danger-300 bg-danger-50 px-2.5 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
          >
            Delete Palette
          </button>
        </div>
      </div>

      <AddColorForm paletteId={palette.id} onAdded={onChanged} />

      {palette.colors.length > 8 && (
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name or hex…"
        />
      )}

      {palette.colors.length === 0 ? (
        <div className="rounded-md border border-dashed border-border py-10 text-center text-sm text-text-tertiary">
          No colors yet — add one above or use Bulk Import.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-secondary/50 text-left text-text-secondary">
                <th className="px-4 py-2.5 font-medium">Color</th>
                <th className="px-4 py-2.5 font-medium">Hex</th>
                <th className="px-4 py-2.5 font-medium">Secondary Hex</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-surface-secondary/40">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Swatch hex={c.hexCode} hex2={c.hexCode2} />
                      <span className="font-medium text-text-primary">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-text-secondary">{c.hexCode}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-text-secondary">
                    {c.hexCode2 ?? '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant={c.isActive ? 'success' : 'default'} size="sm">
                      {c.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => onEditEntry(c)}>
                        Edit
                      </Button>
                      <button
                        type="button"
                        onClick={() => onDeleteEntry(c)}
                        className="inline-flex items-center rounded-md border border-danger-300 bg-danger-50 px-2.5 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-text-tertiary">
                    No colors match “{filter}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single add form — name + hex with live preview + name-map auto-suggest
// ---------------------------------------------------------------------------

function AddColorForm({ paletteId, onAdded }: { paletteId: string; onAdded: () => void }) {
  const [name, setName] = useState('');
  const [hex, setHex] = useState('');
  const [hexTouched, setHexTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: (body: { name: string; hexCode: string }) =>
      api.post(`/api/v1/admin/colors/palettes/${paletteId}/entries`, body),
    onSuccess: () => {
      toast('Color added.');
      setName('');
      setHex('');
      setHexTouched(false);
      setError(null);
      onAdded();
    },
    onError: (err: Error) => setError(err.message || 'Failed to add color.'),
  });

  const handleNameChange = (val: string) => {
    setName(val);
    // Auto-suggest from the shared name map until the admin edits the hex
    // manually. The server-side resolver stays authoritative for detection.
    if (!hexTouched) {
      const suggested = resolveColorNameToHex(val);
      if (suggested) setHex(suggested);
    }
  };

  const isValidHex = HEX_RE.test(hex.trim());
  const canSubmit = name.trim().length > 0 && isValidHex && !addMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    addMutation.mutate({ name: name.trim(), hexCode: hex.trim().toUpperCase() });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-border bg-surface-secondary/40 p-3"
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-44 flex-1">
          <label className="mb-1 block text-xs font-medium text-text-secondary">Color Name</label>
          <Input
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g. Forest Green"
            maxLength={100}
          />
        </div>
        <div className="w-40">
          <label className="mb-1 block text-xs font-medium text-text-secondary">Hex</label>
          <div className="flex items-center gap-2">
            <Swatch hex={isValidHex ? hex.trim().toUpperCase() : '#FFFFFF'} />
            <Input
              value={hex}
              onChange={(e) => {
                setHex(e.target.value);
                setHexTouched(true);
              }}
              placeholder="#228B22"
              maxLength={7}
              className="font-mono"
            />
          </div>
        </div>
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {addMutation.isPending ? 'Adding...' : 'Add Color'}
        </Button>
      </div>
      {hex && !isValidHex && (
        <p className="mt-1.5 text-xs text-warning-dark">Hex must look like #RRGGBB.</p>
      )}
      {error && <div className="mt-2"><AlertBanner type="error" message={error} /></div>}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Create palette modal
// ---------------------------------------------------------------------------

function CreatePaletteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      api.post<PaletteDetail>('/api/v1/admin/colors/palettes', body),
    onSuccess: (res) => onCreated(res.id),
    onError: (err: Error) => setError(err.message || 'Failed to create palette.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Palette name is required.');
      return;
    }
    setError(null);
    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  return (
    <Modal open onClose={onClose} title="New Color Palette" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary">Name *</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Gildan, Comfort Colors"
            maxLength={100}
          />
          <p className="mt-1 text-xs text-text-tertiary">
            One palette per brand chart — the same color name can have a
            different hex in each brand's palette.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary">Description</label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            maxLength={500}
          />
        </div>
        {error && <AlertBanner type="error" message={error} />}
        <ModalFooter>
          <Button type="button" size="sm" variant="outline" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Edit entry modal
// ---------------------------------------------------------------------------

function EditEntryModal({
  entry,
  onClose,
  onSaved,
}: {
  entry: ColorEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(entry.name);
  const [hex, setHex] = useState(entry.hexCode);
  const [hex2, setHex2] = useState(entry.hexCode2 ?? '');
  const [isActive, setIsActive] = useState(entry.isActive);
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: (body: {
      name: string;
      hexCode: string;
      hexCode2: string | null;
      isActive: boolean;
    }) => api.patch(`/api/v1/admin/colors/entries/${entry.id}`, body),
    onSuccess: () => {
      toast('Color updated.');
      onSaved();
    },
    onError: (err: Error) => setError(err.message || 'Failed to update color.'),
  });

  const isValidHex = HEX_RE.test(hex.trim());
  const isValidHex2 = !hex2.trim() || HEX_RE.test(hex2.trim());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !isValidHex || !isValidHex2) {
      setError('Name and a valid #RRGGBB hex are required.');
      return;
    }
    setError(null);
    saveMutation.mutate({
      name: name.trim(),
      hexCode: hex.trim().toUpperCase(),
      hexCode2: hex2.trim() ? hex2.trim().toUpperCase() : null,
      isActive,
    });
  };

  return (
    <Modal open onClose={onClose} title="Edit Color" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-text-secondary">Name *</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Hex *</label>
            <div className="flex items-center gap-2">
              <Swatch hex={isValidHex ? hex.trim().toUpperCase() : '#FFFFFF'} />
              <Input
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                maxLength={7}
                className="font-mono"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">
              Secondary Hex
            </label>
            <div className="flex items-center gap-2">
              <Swatch
                hex={isValidHex ? hex.trim().toUpperCase() : '#FFFFFF'}
                hex2={isValidHex2 && hex2.trim() ? hex2.trim().toUpperCase() : null}
              />
              <Input
                value={hex2}
                onChange={(e) => setHex2(e.target.value)}
                placeholder="Heather…"
                maxLength={7}
                className="font-mono"
              />
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          Active (available to the resolver and auto-detect)
        </label>
        {error && <AlertBanner type="error" message={error} />}
        <ModalFooter>
          <Button type="button" size="sm" variant="outline" onClick={onClose} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Bulk import modal — paste → dry-run preview → commit
// ---------------------------------------------------------------------------

const LINE_STATUS_STYLE: Record<BulkImportLine['status'], { label: string; className: string }> = {
  valid: { label: 'New', className: 'text-success-700 bg-success-50 border-success-300' },
  invalid: { label: 'Invalid', className: 'text-danger-700 bg-danger-50 border-danger-300' },
  'duplicate-in-input': {
    label: 'Duplicate (input)',
    className: 'text-warning-dark bg-warning-light border-warning/40',
  },
  'duplicate-in-palette': {
    label: 'Already in palette',
    className: 'text-warning-dark bg-warning-light border-warning/40',
  },
};

function BulkImportModal({
  paletteId,
  onClose,
  onImported,
}: {
  paletteId: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const [text, setText] = useState('');
  const [report, setReport] = useState<BulkImportReport | null>(null);
  const [duplicateStrategy, setDuplicateStrategy] = useState<'skip' | 'update'>('skip');
  const [error, setError] = useState<string | null>(null);

  const previewMutation = useMutation({
    mutationFn: () =>
      api.post<BulkImportReport>(`/api/v1/admin/colors/palettes/${paletteId}/bulk-import`, {
        text,
        dryRun: true,
      }),
    onSuccess: (res) => {
      setReport(res);
      setError(null);
    },
    onError: (err: Error) => setError(err.message || 'Failed to parse input.'),
  });

  const importMutation = useMutation({
    mutationFn: () =>
      api.post<BulkImportReport>(`/api/v1/admin/colors/palettes/${paletteId}/bulk-import`, {
        text,
        dryRun: false,
        duplicateStrategy,
      }),
    onSuccess: (res) => {
      toast(
        `Import complete: ${res.created ?? 0} created, ${res.updated ?? 0} updated, ${res.skipped ?? 0} skipped.`,
      );
      onImported();
    },
    onError: (err: Error) => setError(err.message || 'Import failed.'),
  });

  const busy = previewMutation.isPending || importMutation.isPending;

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      title="Bulk Import Colors"
      description='Paste "Name: #HEX" pairs separated by semicolons or new lines, e.g. "Black: #25282A; White: #FFFFFF".'
      size="lg"
    >
      <div className="space-y-4">
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setReport(null);
          }}
          rows={6}
          placeholder={'Black: #25282A\nWhite: #FFFFFF\nForest Green: #228B22'}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 font-mono text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />

        {report && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="success" size="sm">{report.validCount} new</Badge>
              <Badge variant="warning" size="sm">{report.duplicateCount} duplicate</Badge>
              <Badge variant="danger" size="sm">{report.invalidCount} invalid</Badge>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface-secondary">
                  <tr className="border-b border-border text-left text-xs text-text-secondary">
                    <th className="px-3 py-2 font-medium">#</th>
                    <th className="px-3 py-2 font-medium">Color</th>
                    <th className="px-3 py-2 font-medium">Hex</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.lines.map((l) => {
                    const style = LINE_STATUS_STYLE[l.status];
                    return (
                      <tr key={l.line} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 text-xs text-text-tertiary">{l.line}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            {l.hexCode && <Swatch hex={l.hexCode} size="sm" />}
                            <span className="text-text-primary">{l.name ?? l.raw}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-text-secondary">
                          {l.hexCode ?? '—'}
                          {l.status === 'duplicate-in-palette' &&
                            l.existingHexCode &&
                            l.existingHexCode !== l.hexCode && (
                              <span className="ml-1 text-text-tertiary">
                                (current: {l.existingHexCode})
                              </span>
                            )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex rounded border px-1.5 py-0.5 text-[11px] font-medium ${style.className}`}
                            title={l.error}
                          >
                            {style.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {report.duplicateCount > 0 && (
              <div className="flex items-center gap-4 rounded-md border border-border bg-surface-secondary/40 px-3 py-2 text-sm">
                <span className="text-text-secondary">Existing names:</span>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={duplicateStrategy === 'skip'}
                    onChange={() => setDuplicateStrategy('skip')}
                  />
                  <span>Skip (keep current hex)</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    checked={duplicateStrategy === 'update'}
                    onChange={() => setDuplicateStrategy('update')}
                  />
                  <span>Update hex to pasted value</span>
                </label>
              </div>
            )}
          </div>
        )}

        {error && <AlertBanner type="error" message={error} />}

        <ModalFooter>
          <Button type="button" size="sm" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {!report ? (
            <Button
              type="button"
              size="sm"
              onClick={() => previewMutation.mutate()}
              disabled={busy || !text.trim()}
            >
              {previewMutation.isPending ? 'Parsing...' : 'Preview'}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={() => importMutation.mutate()}
              disabled={
                busy ||
                (report.validCount === 0 &&
                  !(duplicateStrategy === 'update' && report.duplicateCount > 0))
              }
            >
              {importMutation.isPending
                ? 'Importing...'
                : `Import ${report.validCount} color${report.validCount === 1 ? '' : 's'}`}
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
}
